import axios from 'axios';

function chooseServer(typeLetter, typeServer) {
    if (typeLetter === 'polza') {
        return '/polza/annotate';
    } else if (typeLetter === 'sizes') {
        return '/sizes/annotate';
    }

    switch (typeServer) {
        case 'localhost': {
            if (typeLetter === 'cargo') {
                return 'http://localhost:5000/annotate';
            }
            return 'http://localhost:5001/annotate';
        }
        case 's4.anotatr.com': {
            if (typeLetter === 'cargo') {
                return '/s4/cargo/annotate';
            }
            return '/s4/vessel/annotate';
        }
        case 'nata.im':
            return `/servnata/api${typeLetter}/annotate`;
        case 's11.anotatr.com':
            if (typeLetter === 'cargo') {
                return '/s11/cargo/annotate';
            }
            return '/s11/vessel/annotate';

        case 's1.anotatr.com':
            if (typeLetter === 'cargo') {
                return '/s1/cargo/annotate';
            }
            return '/s1/vessel/annotate';

        case '94.130.128.45': {
            if (typeLetter === 'cargo') {
                return 'http://94.130.128.45:8086/annotate';
            }
            return 'http://94.130.128.45:8085/annotate';
        }
        case '1-shipdev.me': {
            if (typeLetter === 'cargo') {
                return '/shipdev/cargo/annotate';
            }
            return '/shipdev/vessel/annotate';
        }
        case 'polza': {
            if (typeLetter === 'cargo') {
                return '/polza/cargo/annotate';
            }
            return '/polza/vessel/annotate';
        }
        case 'mailparse.net':
        default:
            {
                return `/${typeLetter}/annotate`;
            }
    }
}

export function postAnnotations(
    text,
    typeLetter,
    typeServer,
    options = {},
) {
    options = Object.assign(options, { timeout: 40 });
    const url = chooseServer(typeLetter, typeServer);

    const { isSimpleForm = undefined, subject = undefined, date = undefined } = options;

    return axios({
        method: 'POST',
        url,
        params: options,
        data: {
            isSimpleForm: isSimpleForm || undefined,
            japeLimitsFail: false,
            text,
            subject,
            date,
        },
    })
        .then(response => response)
        .catch((error) => {
            if (error && error.response) {
                return error.response;
            }

            return {
                status: 0,
                data: {
                    error: (error && error.message) || 'Network error',
                    reason: error && error.code === 'ECONNABORTED' ? 'timeout' : 'network',
                },
                message: error && error.message,
            };
        });
}

const REASON_LABELS = {
    capReached: 'JAPE annotation limit reached',
    timeout: 'Annotation request timed out',
    network: 'Could not reach the annotation server',
    halted: 'Annotation was halted',
};

function firstCauseLine(cause) {
    if (!cause) {
        return '';
    }

    return String(cause).split('\n')[0].trim();
}

function stripJavaExceptionPrefix(text) {
    return String(text).replace(/^(?:[a-z0-9_.]+\.)*[A-Za-z]+Exception:\s*/i, '');
}

/**
 * Build a readable annotation-failure message from /annotate error payloads.
 * Handles HTTP error bodies, task-level `{ status: 'failed' }` responses, and network errors.
 */
export function getAnnotationErrorMessage(response) {
    const data = (response && typeof response.data === 'object' && response.data) || response || {};
    const parts = [];

    const capReason = data.japeLimitsCapReason
        || data.capReason
        || (data.japeMetrics && data.japeMetrics.text && data.japeMetrics.text.capReason);

    if (capReason) {
        parts.push(capReason);
    } else if (data.reason && REASON_LABELS[data.reason]) {
        parts.push(REASON_LABELS[data.reason]);
    } else if (data.reason) {
        parts.push(data.reason);
    }

    if (data.error) {
        const shortError = stripJavaExceptionPrefix(data.error);
        if (shortError && !parts.some(part => part.includes(shortError))) {
            parts.push(shortError);
        }
    }

    if (!parts.length) {
        const causeLine = firstCauseLine(data.cause);
        if (causeLine) {
            parts.push(stripJavaExceptionPrefix(causeLine));
        }
    }

    if (!parts.length && (data.message || (response && response.message))) {
        parts.push(data.message || response.message);
    }

    if (!parts.length && response && response.status) {
        const statusText = response.statusText ? ` ${response.statusText}` : '';
        parts.push(`Request failed with status ${response.status}${statusText}`);
    }

    return parts.filter(Boolean).join('. ') || 'Annotation request failed.';
}

export function getAnnotationErrorTitle(response) {
    const data = (response && typeof response.data === 'object' && response.data) || response || {};

    if (data.japeLimitsCapped || data.reason === 'capReached' || data.japeLimitsCapReason || data.capReason) {
        return 'Annotation limit reached';
    }

    if (data.reason === 'timeout' || (response && response.code === 'ECONNABORTED')) {
        return 'Annotation timed out';
    }

    if (data.reason === 'network') {
        return 'Annotation server unreachable';
    }

    return 'Annotation failed';
}

export function isAnnotationFailure(response) {
    const data = (response && typeof response.data === 'object' && response.data) || {};

    if (!response || response.status !== 200) {
        return true;
    }

    if (data.status === 'failed' || data.executedWithError) {
        return true;
    }

    if (data.executionStats && data.executionStats.executedWithError) {
        return true;
    }

    return !Array.isArray(data.annotations);
}

export function getFilters(user) {
    return axios({
        method: 'GET',
        url: '/get/',
        data: {
            id: user._id,
        },
    })
        .then(response => response)
        .catch(response => response.response.data);
}

export function getFilter(user, filter) {
    return axios({
        method: 'GET',
        url: '/get/',
        data: {
            id: user._id,
        },
    })
        .then(response => response)
        .catch(response => response.response.data);
}

export function updateFilter(id, filter) {
    return axios({
        method: 'POST',
        url: '/get/',
        data: {
            id: user._id,
        },
    })
        .then(response => response)
        .catch(response => response.response.data);
}

export function deleteFilter(id, filter) {
    return axios({
        method: 'POST',
        url: '/get/',
        data: {
            id: user._id,
        },
    })
        .then(response => response)
        .catch(response => response.response.data);
}

export function addFilter(id, filter) {
    return axios({
        method: 'POST',
        url: '/get/',
        data: {
            id: user._id,
        },
    })
        .then(response => response)
        .catch(response => response.response.data);
}



// WEBPACK FOOTER //
// ./app/services/annotations.js